// Role-based access control types
export type UserRole = 'viewer' | 'project-editor' | 'document-budget-editor' | 'safety' | 'site-admin' | 'management' | 'administrator';

export interface RolePermissions {
  canViewOverview: boolean;
  canViewDashboard: boolean;
  canViewProjects: boolean;
  canEditProjects: boolean;
  canViewSchedule: boolean;
  canEditSchedule: boolean;
  canViewBudgetRAB: boolean;
  canEditBudgetRAB: boolean;
  canViewDocuments: boolean;
  canEditDocuments: boolean;
  canViewReports: boolean;
  canEditReports: boolean;
  canEditSafety: boolean;
  canPrintReports: boolean;
  canViewTeams: boolean;
  canEditTeams: boolean;
  canViewSettings: boolean;
  canEditSettings: boolean;
  canDeleteAny: boolean;
  canCreateAny: boolean;
  canViewAllSites: boolean;
  isSiteRestricted: boolean;
}

export const ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  viewer: {
    canViewOverview: true,
    canViewDashboard: true,
    canViewProjects: true,
    canEditProjects: false,
    canViewSchedule: true,
    canEditSchedule: false,
    canViewBudgetRAB: false,
    canEditBudgetRAB: false,
    canViewDocuments: true,
    canEditDocuments: false,
    canViewReports: true,
    canEditReports: false,
    canEditSafety: false,
    canPrintReports: false,
    canViewTeams: true,
    canEditTeams: false,
    canViewSettings: true,
    canEditSettings: false,
    canDeleteAny: false,
    canCreateAny: false,
    canViewAllSites: false,
    isSiteRestricted: true,
  },
  'project-editor': {
    canViewOverview: true,
    canViewDashboard: true,
    canViewProjects: true,
    canEditProjects: true,
    canViewSchedule: true,
    canEditSchedule: true,
    canViewBudgetRAB: false,
    canEditBudgetRAB: false,
    canViewDocuments: true,
    canEditDocuments: false,
    canViewReports: true,
    canEditReports: true,
    canEditSafety: false,
    canPrintReports: true,
    canViewTeams: true,
    canEditTeams: false,
    canViewSettings: true,
    canEditSettings: false,
    canDeleteAny: false,
    canCreateAny: true,
    canViewAllSites: false,
    isSiteRestricted: true,
  },
  'document-budget-editor': {
    canViewOverview: true,
    canViewDashboard: true,
    canViewProjects: true,
    canEditProjects: false,
    canViewSchedule: true,
    canEditSchedule: false,
    canViewBudgetRAB: true,
    canEditBudgetRAB: true,
    canViewDocuments: true,
    canEditDocuments: true,
    canViewReports: true,
    canEditReports: false,
    canEditSafety: false,
    canPrintReports: true,
    canViewTeams: true,
    canEditTeams: false,
    canViewSettings: true,
    canEditSettings: false,
    canDeleteAny: false,
    canCreateAny: true,
    canViewAllSites: false,
    isSiteRestricted: true,
  },
  'safety': {
    canViewOverview: true,
    canViewDashboard: true,
    canViewProjects: true,
    canEditProjects: false,
    canViewSchedule: true,
    canEditSchedule: false,
    canViewBudgetRAB: false,
    canEditBudgetRAB: false,
    canViewDocuments: true,
    canEditDocuments: false,
    canViewReports: true,
    canEditReports: false,
    canEditSafety: true,
    canPrintReports: false,
    canViewTeams: true,
    canEditTeams: false,
    canViewSettings: true,
    canEditSettings: false,
    canDeleteAny: false,
    canCreateAny: false,
    canViewAllSites: false,
    isSiteRestricted: true,
  },
  'site-admin': {
    canViewOverview: true,
    canViewDashboard: true,
    canViewProjects: true,
    canEditProjects: true,
    canViewSchedule: true,
    canEditSchedule: true,
    canViewBudgetRAB: true,
    canEditBudgetRAB: true,
    canViewDocuments: true,
    canEditDocuments: true,
    canViewReports: true,
    canEditReports: true,
    canEditSafety: true,
    canPrintReports: true,
    canViewTeams: true,
    canEditTeams: true,
    canViewSettings: true,
    canEditSettings: true,
    canDeleteAny: true,
    canCreateAny: true,
    canViewAllSites: false,
    isSiteRestricted: true, // Restricted to their own site
  },
  'management': {
    canViewOverview: true,
    canViewDashboard: true,
    canViewProjects: true,
    canEditProjects: false,
    canViewSchedule: true,
    canEditSchedule: false,
    canViewBudgetRAB: true,
    canEditBudgetRAB: false,
    canViewDocuments: true,
    canEditDocuments: false,
    canViewReports: true,
    canEditReports: false,
    canEditSafety: false,
    canPrintReports: true,
    canViewTeams: true,
    canEditTeams: false,
    canViewSettings: true,
    canEditSettings: false,
    canDeleteAny: false,
    canCreateAny: false,
    canViewAllSites: true, // Can view all sites
    isSiteRestricted: false,
  },
  administrator: {
    canViewOverview: true,
    canViewDashboard: true,
    canViewProjects: true,
    canEditProjects: true,
    canViewSchedule: true,
    canEditSchedule: true,
    canViewBudgetRAB: true,
    canEditBudgetRAB: true,
    canViewDocuments: true,
    canEditDocuments: true,
    canViewReports: true,
    canEditReports: true,
    canEditSafety: true,
    canPrintReports: true,
    canViewTeams: true,
    canEditTeams: true,
    canViewSettings: true,
    canEditSettings: true,
    canDeleteAny: true,
    canCreateAny: true,
    canViewAllSites: true,
    isSiteRestricted: false,
  },
};

export interface Task {
  id: string;
  title: string;
  description: string;
  status: 'todo' | 'in-progress' | 'completed' | 'blocked';
  priority: 'low' | 'medium' | 'high';
  dueDate?: string;
  assignedTo?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MemberTask extends Task {
  completed: boolean;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  position?: string;
  role: UserRole;
  department?: string;
  phone?: string;
  status: 'active' | 'inactive' | 'on-leave';
  joinDate?: string;
  projectsAssigned?: number;
  skills?: string[];
  experience?: string;
  reportTo?: string | null;
  imageUrl?: string;
  is_admin?: boolean;
  siteOfficeId?: string | null;
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  // Password fields removed - authentication now handled by Supabase Auth
  approvedBy?: string;
  approvedAt?: string;
  registrationCode?: string;
  createdAt?: string;
  updatedAt?: string;
  tasks?: any[];
  // Block fields for administrator control
  isBlocked?: boolean;
  blockedAt?: string;
  blockedBy?: string;
  blockReason?: string;
}

// Added missing TeamStats export
export interface TeamStats {
  totalMembers: number;
  activeMembers: number;
  departments: string[];
  averageExperience: number;
}
